import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExternalAssetListGroupDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import {
  EXTERNAL_NETWORK_ASSET_KINDS,
  SURFACE_ASSETS_KEY,
  fetchExternalNetworkAllKindsPage,
  fetchExternalNetworkAssets,
  useExternalNetworkKindTotals,
} from '../../../../hooks/api/useSurfaceAssets';
import {
  assetListFilterQueryKey,
  kindSupportsAllAssetFilters,
  type ExternalNetworkAssetListFilters,
} from '../build-asset-list-query';
import {
  EXTERNAL_ASSETS_PAGE_SIZE,
  resolveFirstAllKindsPageParam,
  resolveNextAllKindsPageParam,
  toExternalAssetsOrderBy,
  type ExternalAssetsAllKindsPageParam,
  type ExternalAssetsGroupByField,
  type ExternalAssetsSort,
} from './external-assets-table.model';

const EXTERNAL_ASSETS_PAGES_KEY = 'external-pages';
const EXTERNAL_ASSETS_ALL_KINDS_PAGES_KEY = 'external-all-kinds-pages';

export type ExternalAssetEntry = {
  entityKind: ExternalNetworkEntityKind;
  asset: object;
};

export function useExternalAssetsListData({
  surfaceId,
  selectedKind,
  search,
  filters,
  sort,
  groupBy,
  includeKindTotals,
}: {
  surfaceId: string;
  selectedKind: ExternalNetworkEntityKind | null;
  search: string;
  filters: ExternalNetworkAssetListFilters;
  sort: ExternalAssetsSort;
  groupBy: ExternalAssetsGroupByField | undefined;
  includeKindTotals: boolean;
}) {
  const queryClient = useQueryClient();
  const orderBy = useMemo(() => toExternalAssetsOrderBy(sort), [sort]);
  const isSingleKind = selectedKind !== null;
  const supportsKind = useCallback(
    (entityKind: ExternalNetworkEntityKind) =>
      kindSupportsAllAssetFilters({ entityKind, filters }),
    [filters],
  );
  const firstAllKindsPageParam = resolveFirstAllKindsPageParam({
    kinds: EXTERNAL_NETWORK_ASSET_KINDS,
    supportsKind,
  });
  const allKindsInitialPageParam: ExternalAssetsAllKindsPageParam =
    firstAllKindsPageParam ?? { entityKind: 'DOMAIN', skip: 0 };

  const pagesQuery = useInfiniteQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      EXTERNAL_ASSETS_PAGES_KEY,
      surfaceId,
      selectedKind,
      search,
      orderBy ?? null,
      groupBy ?? null,
      ...assetListFilterQueryKey(filters),
    ],
    queryFn: ({ pageParam }) =>
      fetchExternalNetworkAssets({
        surfaceId,
        entityKind: selectedKind ?? 'DOMAIN',
        search,
        skip: pageParam,
        limit: EXTERNAL_ASSETS_PAGE_SIZE,
        filters,
        orderBy,
        groupBy,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce(
        (sum, page) => sum + page.data.length,
        0,
      );

      return lastPage.data.length > 0 && loadedCount < lastPage.total
        ? loadedCount
        : undefined;
    },
    enabled: Boolean(surfaceId) && isSingleKind,
  });

  const allKindsPagesQuery = useInfiniteQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      EXTERNAL_ASSETS_ALL_KINDS_PAGES_KEY,
      surfaceId,
      search,
      orderBy ?? null,
      ...assetListFilterQueryKey(filters),
    ],
    queryFn: ({ pageParam }) =>
      fetchExternalNetworkAllKindsPage({
        surfaceId,
        search,
        filters,
        orderBy,
        pageParam,
      }),
    initialPageParam: allKindsInitialPageParam,
    getNextPageParam: (lastPage) =>
      resolveNextAllKindsPageParam({
        kinds: EXTERNAL_NETWORK_ASSET_KINDS,
        supportsKind,
        lastPage: {
          entityKind: lastPage.entityKind,
          skip: lastPage.skip,
          rowCount: lastPage.data.length,
          total: lastPage.total,
        },
      }),
    enabled:
      Boolean(surfaceId) &&
      !isSingleKind &&
      firstAllKindsPageParam !== undefined,
  });

  const kindTotalsQuery = useExternalNetworkKindTotals({
    surfaceId,
    search,
    filters,
    enabled: !isSingleKind || includeKindTotals,
  });

  const entries = useMemo((): ExternalAssetEntry[] => {
    if (selectedKind !== null) {
      return (pagesQuery.data?.pages ?? []).flatMap((page) =>
        page.data.map((asset) => ({ entityKind: selectedKind, asset })),
      );
    }

    return (allKindsPagesQuery.data?.pages ?? []).flatMap((page) =>
      page.data.map((asset) => ({ entityKind: page.entityKind, asset })),
    );
  }, [allKindsPagesQuery.data, pagesQuery.data, selectedKind]);

  const firstPage = pagesQuery.data?.pages[0];
  const groups: readonly ExternalAssetListGroupDto[] = groupBy
    ? (firstPage?.groups ?? [])
    : [];

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
  }, [queryClient]);
  const activePagesQuery = isSingleKind ? pagesQuery : allKindsPagesQuery;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = activePagesQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    entries,
    kindTotals: kindTotalsQuery.totals,
    kindTotalsSum: kindTotalsQuery.total,
    groups,
    total: isSingleKind ? (firstPage?.total ?? 0) : kindTotalsQuery.total,
    isLoading: isSingleKind
      ? pagesQuery.isLoading
      : allKindsPagesQuery.isLoading || kindTotalsQuery.isLoading,
    isError: isSingleKind
      ? pagesQuery.isError
      : allKindsPagesQuery.isError || kindTotalsQuery.isError,
    isFetchingMore: isFetchingNextPage,
    hasMore: Boolean(hasNextPage),
    loadMore,
    refresh,
  };
}
